import React from 'react';

import {
  Card,
  FormItem,
  Toggle,
  Input,
  AudioPlayer,
  Dropdown,
} from '../../../components';
import { Slider } from '../../../components/Slider';
import type { AgentForm as AgentFormType } from '../../../utilities';
import {
  ambientSoundOptions,
  thinkingSoundOptions,
  CUSTOM_URL_VALUE,
  backgroundAudioVolumeStepOptions,
} from '../const';
import {
  hasSupportedAudioExtension,
  isValidHttpUrl,
  validateBackgroundAudioUrl,
} from '../utils/backgroundAudio';

export type BackgroundAudioSettingsProps = {
  disabled?: boolean;
  form: AgentFormType;
};

export const BackgroundAudioSettings: React.FC<
  BackgroundAudioSettingsProps
> = ({ disabled, form }) => {
  const isAmbientCustomSelected =
    form.background_audio_ambient_sound === CUSTOM_URL_VALUE;
  const isThinkingCustomSelected =
    form.background_audio_thinking_sound === CUSTOM_URL_VALUE;
  const hasAmbientPreset =
    form.background_audio_ambient_sound !== null &&
    form.background_audio_ambient_sound !== undefined &&
    form.background_audio_ambient_sound !== CUSTOM_URL_VALUE;
  const hasThinkingPreset =
    form.background_audio_thinking_sound !== null &&
    form.background_audio_thinking_sound !== undefined &&
    form.background_audio_thinking_sound !== CUSTOM_URL_VALUE;
  const hasAmbientCustomUrl =
    isAmbientCustomSelected &&
    !!form.background_audio_ambient_custom_url &&
    form.background_audio_ambient_custom_url.trim().length > 0;
  const hasThinkingCustomUrl =
    isThinkingCustomSelected &&
    !!form.background_audio_thinking_custom_url &&
    form.background_audio_thinking_custom_url.trim().length > 0;
  const showAmbientVolume = hasAmbientPreset || hasAmbientCustomUrl;
  const showThinkingVolume = hasThinkingPreset || hasThinkingCustomUrl;
  const canPreviewAmbient =
    isAmbientCustomSelected &&
    isValidHttpUrl(form.background_audio_ambient_custom_url) &&
    hasSupportedAudioExtension(form.background_audio_ambient_custom_url);
  const canPreviewThinking =
    isThinkingCustomSelected &&
    isValidHttpUrl(form.background_audio_thinking_custom_url) &&
    hasSupportedAudioExtension(form.background_audio_thinking_custom_url);

  return (
    <Card
      collapsable
      size="unset"
      title="Background Audio"
      name="agentpage.backgroundAudio"
      contentClassName="flex flex-col gap-y-md"
      subtitle="Add ambient and thinking sounds to make conversations feel more natural"
    >
      <FormItem<AgentFormType>
        name="background_audio_enabled"
        label="Enable Background Audio"
        className="flex justify-between items-center"
      >
        <Toggle disabled={disabled} />
      </FormItem>

      {form.background_audio_enabled && (
        <>
          <FormItem<AgentFormType>
            name="background_audio_ambient_sound"
            label="Ambient Sound"
          >
            <Dropdown options={ambientSoundOptions} disabled={disabled} />
          </FormItem>
          {isAmbientCustomSelected && canPreviewAmbient && (
            <div className="sm:ml-[10rem] md:ml-[18rem] max-w-[32rem] w-full">
              <AudioPlayer
                src={form.background_audio_ambient_custom_url || ''}
              />
            </div>
          )}
          {isAmbientCustomSelected && (
            <FormItem<AgentFormType>
              name="background_audio_ambient_custom_url"
              label="Custom Ambient Sound URL"
              validate={validateBackgroundAudioUrl}
              errorBelowInput
            >
              <Input
                disabled={disabled}
                placeholder="https://example.com/ambient.wav"
              />
            </FormItem>
          )}

          {showAmbientVolume && (
            <FormItem<AgentFormType>
              name="background_audio_ambient_volume"
              label="Ambient Volume"
            >
              <Slider
                min={0}
                max={1}
                step={0.1}
                stepOptions={backgroundAudioVolumeStepOptions}
              />
            </FormItem>
          )}

          <FormItem<AgentFormType>
            name="background_audio_thinking_sound"
            label="Thinking Sound"
          >
            <Dropdown options={thinkingSoundOptions} disabled={disabled} />
          </FormItem>
          {isThinkingCustomSelected && canPreviewThinking && (
            <div className="sm:ml-[10rem] md:ml-[18rem] max-w-[32rem] w-full">
              <AudioPlayer
                src={form.background_audio_thinking_custom_url || ''}
              />
            </div>
          )}
          {isThinkingCustomSelected && (
            <FormItem<AgentFormType>
              name="background_audio_thinking_custom_url"
              label="Custom Thinking Sound URL"
              validate={validateBackgroundAudioUrl}
              errorBelowInput
            >
              <Input
                disabled={disabled}
                placeholder="https://example.com/thinking.wav"
              />
            </FormItem>
          )}

          {showThinkingVolume && (
            <FormItem<AgentFormType>
              name="background_audio_thinking_volume"
              label="Thinking Volume"
            >
              <Slider
                min={0}
                max={1}
                step={0.1}
                stepOptions={backgroundAudioVolumeStepOptions}
              />
            </FormItem>
          )}
        </>
      )}
    </Card>
  );
};
